import styled from "styled-components";
import { useEffect, useState } from "react";
import { ParticipantEvent, Track } from "livekit-client";
import { Overlay } from "./Overlay/Overlay";
import ParticipantTracks from "../ParticipantTracks/ParticipantTracks";
import { ObserverParticipant } from "../../Observe/ObserverParticipant";
import { colors } from "utils";
import { useSessionContext } from "hooks/useSession";
import { useOkoSessionContext } from "hooks/useOkoSession/useOkoSession";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";
import { CameraOffFace } from "./CameraOffFace";
import HandButton from "assets/Button_Handraise.svg"

export const Participant = ({
  participant,
  videoOnly,
  enableScreenShare,
  isLocalParticipant,
  isSpectator,
  participantIndex = 0,
}) => {
  const { session } = useSessionContext();
  const { calculatorSession: { isAllowingGesture }} = useOkoSessionContext();
  const [isAudioMuted, setIsAudioMuted] = useState(
    Array.from(participant?.trackPublications?.values())?.find((trackPublication) =>
      trackPublication.source.includes(Track.Source.Microphone))?.track?.isMuted
  );
  const [isVideoMuted, setIsVideoMuted] = useState(true); // less flickering if video disabled
  const [isManualGestureTriggered, setIsManualGestureTriggered] = useState();

  const handleManualGesture = async (gesture_type) => {
    if (isManualGestureTriggered) return;
    setIsManualGestureTriggered(true);
    await request(`session/${session?.id}/trigger_manual_gesture/${gesture_type}`)
      .then(() => setIsManualGestureTriggered(false))
      .catch((e) => {
        console.error(e);
        setIsManualGestureTriggered(false);
        logError("Failed to trigger manual gesture", e, {
          category: LogCategory.SESSION,
          sessionId: session?.id,
        });
      });
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsAudioMuted(Array.from(participant?.trackPublications?.values())?.find((trackPublication) =>
        trackPublication.source.includes(Track.Source.Microphone))?.track?.isMuted);
      setIsVideoMuted(Array.from(participant?.trackPublications?.values())?.find((trackPublication) =>
        trackPublication.source.includes(Track.Source.Camera))?.track?.isMuted);
    }, 1500); // There is a delay in track publication propagation, especially for video track.

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!participant) return;

    const audioTrack = Array.from(participant?.trackPublications?.values())?.find((trackPublication) =>
        trackPublication.source.includes(Track.Source.Microphone))?.track;
    const videoTrack = Array.from(participant?.trackPublications?.values())?.find((trackPublication) =>
        trackPublication.source.includes(Track.Source.Camera))?.track;
    if (audioTrack) {
      setIsAudioMuted(audioTrack.isMuted);
    }
    if (videoTrack) {
      setIsVideoMuted(videoTrack.isMuted);
    }

    const trackMuted = (publication) => {
      if (publication.source === Track.Source.Microphone) {
        setIsAudioMuted(true);
      } else if (publication.source === Track.Source.Camera) {
        setIsVideoMuted(true);
      }
    };

    const trackUnmuted = (publication) => {
      if (publication.source === Track.Source.Microphone) {
        setIsAudioMuted(false);
      } else if (publication.source === Track.Source.Camera) {
        setIsVideoMuted(false);
      }
    };

    participant.on(ParticipantEvent.TrackMuted, trackMuted);
    participant.on(ParticipantEvent.TrackUnmuted, trackUnmuted);

    return () => {
      participant.off(ParticipantEvent.TrackMuted, trackMuted);
      participant.off(ParticipantEvent.TrackUnmuted, trackUnmuted);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participant, participant?.trackPublications?.values()]);

  let style = {};
  if (isSpectator) style = { overflow: "visible"};

  return (
    <Container style={style}>
      <Content>
        {isSpectator ? (
          <ObserverParticipant
            participant={participant}
            isAudioMuted={isAudioMuted}
            isVideoMuted={isVideoMuted}
          />
        ) : (
          <>
            <div>
              {isVideoMuted && <CameraOffFace index={participantIndex} />}
              {!isManualGestureTriggered &&
                isVideoMuted &&
                isLocalParticipant &&
                isAllowingGesture &&
                <img src={HandButton}
                     alt="Hand Button"
                     style={{ position: "absolute", top: "calc(50% - 32px)", right: "5%", width: "64px", height: "64px" }}
                     onClick={(e) => handleManualGesture("SingleHand")}
                />}
            </div>
            <ParticipantTracks
              participant={participant}
              videoOnly={videoOnly}
              enableScreenShare={enableScreenShare}
              isLocalParticipant={isLocalParticipant}
              isVideoMuted={isVideoMuted}
            />
            <Overlay participant={participant} isAudioMuted={isAudioMuted} />
          </>
        )}
      </Content>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  padding-bottom: ${(9 / 16) * 100}%;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  width: 100%;
  position: relative;
  background: linear-gradient(to top, ${colors.nightlyWoods500}, ${colors.nightlyWoods950});
  });

  & video {
    filter: none;
    object-fit: contain !important;
  }
`;

const Content = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
`;
